import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import NotFound from './components/NotFound';

// ✅ Imports
import ProjectList from './Projects/ListProjects.js';
import ClientList from './Clients/ClientsList.js';
import CreateProject from './Projects/CreateProject.js';
import CreateClient from './Clients/CreateClient.js';
import EditClient from './Clients/EditClient.js';
import EditProject from './Projects/EditProject.js';
import ProjectDet from './Projects/ProjectDetails.js';
import ClientDet from './Clients/ClientDetails.js';
import DeletedProject from './Projects/TrashProjects.js';
import DeletedClient from './Clients/TrashClients.js';
import CreatePartner from './partners/CreatePartner.js';
import ListPartners from './partners/ListPartners.js';
import CollaboratorList from './Collaborators/CollaboratorsList.js';
import CreateCollaborator from './Collaborators/CreateCollaborator.js';
import EditCollaborator from './Collaborators/EditCollaborator.js';
import CollaboratorsDet from './Collaborators/CollaboratorDetails.js';
import DeletedCollaborator from './Collaborators/TrashCollaborators.js';
import Dashboard from './dashboard';
import './login.css';
import pic from './pic.png';
import ListArticles from './Articles/ListArticles.js';
import CreateArticle from './Articles/CreateArticle.js';
import EditArticle from './Articles/EditArticle.js';
import PreviewArticle from './Articles/PreviewArticle.js';
import EditPartner from './partners/EditPartner.js';
import Header from './components/Header.js';
import Footer from './components/Footer.js';
import Home from './Home/Home.js';
import Unauthorized from './components/Unauthorized.js';
import ContentSettings from './Settings/ContentSettings.js';
import ContactMessages from './Contacts/ContactMessages.js';
import SubscriberList from './Subscribers/SubscriberList.js';
import FaqManagement from './Management/FaqManagement.js';
import FeedbackManagement from './Management/FeedbackManagement.js';
import ServiceManagement from './Management/ServiceManagement.js';

// ================== LOGIN COMPONENT ==================
function SuperAdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || 'Erreur lors de la connexion');
        return;
      }

      const data = await response.json();
      localStorage.setItem('token', data.accessToken);
      navigate('/admin/home');
    } catch (err) {
      setError('Erreur réseau');
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-left">
        <div className="login-logo">
          <img className="logo" src={pic} width="120px" alt="Logo" />
        </div>
        <h2>Panneau d’administration</h2>
        <p className="login-subtext">Connectez-vous pour accéder à votre espace</p>
        <form onSubmit={handleSubmit}>
          <div>
            <label>Adresse email</label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </div>
          <div>
            <label>Mot de passe</label>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <button type="submit">Se connecter</button>
        </form>
      </div>
      <div className="login-right">
        <div className="overlay"></div>
        <h1>
          Bienvenue sur le tableau de bord de <strong>Junior Entreprise Insat</strong>
        </h1>
        <p>
          Votre plateforme centralisée pour gérer, analyser et optimiser<br />
          les performances de votre entreprise en temps réel.
        </p>
      </div>
    </div>
  );
}

// ================== LAYOUT (HEADER/FOOTER CONTROL) ==================
function Layout({ children }) {
  return (
    <div>
      <Header />
      {children}
      <Footer />
    </div>
  );
}

// ================== APP ROUTES ==================
function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes: login and forbidden */}
        <Route path="/" element={<SuperAdminLogin />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        {/* Protected routes: wrap with ProtectedRoute. Layout only for valid pages. */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <Routes>
                {/* Dashboard */}
                <Route path="users" element={<Layout><Dashboard /></Layout>} />
                <Route path="home" element={<Layout><Home /></Layout>} />

                {/* Projects */}
                <Route path="projects" element={<Layout><ProjectList /></Layout>} />
                <Route path="projects/new" element={<Layout><CreateProject /></Layout>} />
                <Route path="projects/:id/edit" element={<Layout><EditProject /></Layout>} />
                <Route path="projects/:id" element={<Layout><ProjectDet /></Layout>} />
                <Route path="projects/trash" element={<Layout><DeletedProject /></Layout>} />

                {/* Partners */}
                <Route path="partners" element={<Layout><ListPartners /></Layout>} />
                <Route path="partners/new" element={<Layout><CreatePartner /></Layout>} />
                <Route path="partners/EditPartner/:id" element={<Layout><EditPartner /></Layout>} />

                {/* Clients */}
                <Route path="clients" element={<Layout><ClientList /></Layout>} />
                <Route path="clients/new" element={<Layout><CreateClient /></Layout>} />
                <Route path="clients/:id/edit" element={<Layout><EditClient /></Layout>} />
                <Route path="clients/:id" element={<Layout><ClientDet /></Layout>} />
                <Route path="clients/trash" element={<Layout><DeletedClient /></Layout>} />

                {/* Articles */}
                <Route path="articles" element={<Layout><ListArticles /></Layout>} />
                <Route path="articles/new" element={<Layout><CreateArticle /></Layout>} />
                <Route path="articles/edit/:id" element={<Layout><EditArticle /></Layout>} />
                <Route path="articles/:id" element={<Layout><PreviewArticle /></Layout>} />
                <Route path="settings" element={<Layout><ContentSettings /></Layout>} />
                <Route path="contacts" element={<Layout><ContactMessages /></Layout>} />
                <Route path="subscribers" element={<Layout><SubscriberList /></Layout>} />
                <Route path="faq" element={<Layout><FaqManagement /></Layout>} />
                <Route path="feedback" element={<Layout><FeedbackManagement /></Layout>} />
                <Route path="services" element={<Layout><ServiceManagement /></Layout>} />

                {/* Collaborators */}
                <Route path="collaborators" element={<Layout><CollaboratorList /></Layout>} />
                <Route path="collaborators/new" element={<Layout><CreateCollaborator /></Layout>} />
                <Route path="collaborators/:id/edit" element={<Layout><EditCollaborator /></Layout>} />
                <Route path="collaborators/trash" element={<Layout><DeletedCollaborator /></Layout>} />
                <Route path="collaborators/:id" element={<Layout><CollaboratorsDet /></Layout>} />

                {/* NotFound for protected routes, without Layout */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          }
        />

        {/* Catch-all for public routes (must be last) */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
